import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { publicApi, PublicDashboardData } from '../../services/publicApi';
import { TimelineData, TimelineArticle, TimeRange } from '../../types/timeline';
import { SupportedLanguage } from '../../types/language';
import { InteractiveTimeline } from '../InteractiveTimeline';
import { TimelineArticles } from '../TimelineArticles';
import { ArticleDetailModal } from '../ArticleDetailModal';
import EmailSubscriptionBanner from '../subscription/EmailSubscriptionBanner';
import { isSubscribed } from '../../utils/subscription';

// Public Dashboard Components
import { ThreatFilterSection } from './ThreatFilterSection';
// import { RiskOverviewCard } from './RiskOverviewCard';
import { CategoryBreakdownCard } from './CategoryBreakdownCard';
import { RecentArticlesCard } from './RecentArticlesCard';
import { HelpModal } from './HelpModal';
import { readModalParams } from '../../lib/urls';

const normalizeLocale = (value: string): SupportedLanguage => {
  const normalized =
    (value || 'et').toLowerCase() === 'uk'
      ? 'ua'
      : (value || 'et').toLowerCase();
  if (
    normalized === 'et' ||
    normalized === 'en' ||
    normalized === 'ru' ||
    normalized === 'ua'
  ) {
    return normalized;
  }
  return 'et';
};

export const PublicDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Bootstrap from server-preloaded data when available
  const preloaded: any = (typeof window !== 'undefined' && (window as any).__TC_PRELOADED__) || null;
  const hasPreloaded = Boolean(preloaded?.timeline && preloaded?.dashboard);
  const [loading, setLoading] = useState(!hasPreloaded);
  const [error, setError] = useState<string | null>(null);
  const [timelineData, setTimelineData] = useState<TimelineData | null>(preloaded?.timeline || null);
  const [dashboardData, setDashboardData] = useState<PublicDashboardData | null>(preloaded?.dashboard || null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedArticles, setSelectedArticles] = useState<TimelineArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<TimelineArticle | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [showHelp, setShowHelp] = useState(false);
  const [subscribed, setSubscribed] = useState<boolean>(() => {
    try { return isSubscribed(); } catch { return false; }
  });
  useEffect(() => {
    const i = setInterval(() => {
      try { setSubscribed(isSubscribed()); } catch {}
    }, 2000);
    return () => clearInterval(i);
  }, []);
  const [language, setLanguage] = useState<SupportedLanguage>('et');
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set(['MILITARY'])); // Default to military only
  const [viewMode] = useState<'detailed' | 'simplified'>('simplified');
  const requestRef = useRef(0);
  const mountedRef = useRef(true);

  // Clear preloaded payload after hydration to free memory
  useEffect(() => {
    try { if (preloaded) delete (window as any).__TC_PRELOADED__; } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
    };
  }, []);

  // Initialize and sync language from locale in the path
  useEffect(() => {
    try {
      const parts = window.location.pathname.split('/').filter(Boolean);
      const normalized = normalizeLocale(parts[0] || 'et');
      setLanguage(normalized);
      i18n.changeLanguage(normalized);
    } catch {}
  }, [i18n]);

  // Keep local language state in sync with i18n changes (e.g., dropdown)
  useEffect(() => {
    const loc = (i18n.language || 'et').split('-')[0].toLowerCase();
    setLanguage(normalizeLocale(loc));
  }, [i18n.language]);

  // No need for manual ESC handler now; handled by Modal

  useEffect(() => {
    // Always refresh immediately when filters change, then keep background refresh
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, selectedCategories]);

  // Open Help modal if query contains help=1; sync time range from URL (?range=24h|7d|30d)
  useEffect(() => {
    try {
      const h = searchParams.get('help');
      if (h === '1') setShowHelp(true);
      const r = (searchParams.get('range') || '').toLowerCase();
      if (r === '24h' || r === '7d' || r === '30d') {
        if (r !== timeRange) setTimeRange(r as TimeRange);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchData = async () => {
    const requestId = ++requestRef.current;
    try {
      setLoading(true);
      
      // Calculate date range based on selection
      const endDate = new Date();
      let startDate = new Date();
      
      switch (timeRange) {
        case '24h':
          startDate.setDate(endDate.getDate() - 1);
          break;
        case '7d':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '30d':
        default:
          startDate.setDate(endDate.getDate() - 30);
          break;
      }

      // Convert selectedCategories to array for API
      const categoriesArray = selectedCategories.size > 0 ? Array.from(selectedCategories) : undefined;
      
      const [timeline, dashboard] = await Promise.all([
        publicApi.getTimelineData(startDate.toISOString(), endDate.toISOString(), categoriesArray),
        publicApi.getDashboardData(startDate.toISOString(), endDate.toISOString(), categoriesArray, 'EST')
      ]);
      if (!mountedRef.current || requestId !== requestRef.current) return;

      setTimelineData(timeline);
      setDashboardData(dashboard);
      // After data loads, honor deep-link params
      try {
        const { article: spArticle, date: spDate } = readModalParams(searchParams);
        if (spDate && timeline?.timeline) {
          const day = timeline.timeline.find((d) => d.date === spDate);
          if (day && day.articles?.length) {
            setSelectedDate(spDate);
            setSelectedArticles(day.articles);
          }
        }
        if (spArticle) {
          const id = spArticle;
          const allArticles: TimelineArticle[] = timeline.timeline.flatMap((d) => d.articles || []);
          const found = allArticles.find((a) => String(a.id) === String(id));
          if (found) setSelectedArticle(found);
        }
      } catch {}
      setError(null);
    } catch (err) {
      if (!mountedRef.current || requestId !== requestRef.current) return;
      setError(t('errors.loadThreatData', 'Unable to load threat assessment data. Please try again later.'));
      console.error(err);
    } finally {
      if (!mountedRef.current || requestId !== requestRef.current) return;
      setLoading(false);
    }
  };

  const handleDateSelect = (date: string, articles: TimelineArticle[]) => {
    setSelectedDate(date);
    setSelectedArticles(articles);
    const next = new URLSearchParams(searchParams);
    next.set('date', date);
    setSearchParams(next, { replace: false });
  };

  const handleShowAllCategories = () => {
    setSelectedCategories(new Set(['MILITARY', 'CYBER', 'DIPLOMATIC', 'ECONOMIC', 'INFORMATION_WARFARE']));
  };

  if (loading && !timelineData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading.threatAssessment', 'Loading threat assessment...')}</p>
        </div>
      </div>
    );
  }

  if (error && !timelineData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md">
          <p className="text-gray-800 text-center">{error}</p>
          <button 
            onClick={fetchData}
            className="mt-4 w-full bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700 transition-colors"
          >
            {t('actions.tryAgain', t('verify.tryAgain'))}
          </button>
        </div>
      </div>
    );
  }

  if (!timelineData || !dashboardData) return null;

  // Use server-computed metrics, filtered by selected categories via API (risk summary removed from layout)
  const isMilitaryMode = selectedCategories.size === 1 && selectedCategories.has('MILITARY');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pb-20">
      {!subscribed && <EmailSubscriptionBanner />}
      
      {/* Page-level header removed; top global header is the only header */}

      <ThreatFilterSection
        selectedCategories={selectedCategories}
        onFilterChange={setSelectedCategories}
        language={language}
        onShowHelp={() => setShowHelp(true)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Timeline full width */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">{t('public.timeline.title')}</h3>
            {/* Time range selector colocated with timeline */}
            <div className="flex items-center">
              <div className="inline-flex rounded-md border border-gray-200 bg-white p-1 shadow-sm overflow-x-auto" role="group">
                {(['24h', '7d', '30d'] as TimeRange[]).map((range) => (
                  <button
                    key={range}
                    onClick={() => {
                      setTimeRange(range);
                      try {
                        const next = new URLSearchParams(searchParams);
                        next.set('range', range);
                        setSearchParams(next, { replace: false });
                      } catch {}
                    }}
                    className={`px-3 py-1.5 text-sm font-medium ${
                      timeRange === range ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'
                    } ${range === '24h' ? 'rounded-l-md' : ''} ${range === '30d' ? 'rounded-r-md' : ''} border border-gray-200 focus:z-10 focus:ring-2 focus:ring-blue-500 transition-colors`}
                  >
                    {range === '24h'
                      ? t('public.timeRanges.last24Hours')
                      : range === '7d'
                      ? t('public.timeRanges.lastWeek')
                      : t('public.timeRanges.lastMonth')}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <InteractiveTimeline
            timelineData={timelineData}
            onDateSelect={handleDateSelect}
            selectedDate={selectedDate}
            selectedCategories={selectedCategories}
            language={language}
          />
        </div>

        {/* Category Breakdown below timeline; no timeframe mention */}
        {!isMilitaryMode && (viewMode === 'detailed' || dashboardData.categoryBreakdown.length > 0) && (
          <div className="mb-4">
            <CategoryBreakdownCard
              categoryBreakdown={dashboardData.categoryBreakdown}
              selectedCategories={selectedCategories}
              language={language}
            />
          </div>
        )}

        {/* Recent Articles - keep low priority and below primary insights */}
        <div className="mt-6">
          <RecentArticlesCard
            timelineData={timelineData}
            timeRange={timeRange}
            selectedCategories={selectedCategories}
            viewMode={viewMode}
            language={language}
            onArticleSelect={setSelectedArticle}
            onShowAllCategories={handleShowAllCategories}
          />
        </div>
      </div>

      {/* Timeline Articles Modal */}
      {selectedDate && selectedArticles.length > 0 && (
        <TimelineArticles
          date={selectedDate}
          articles={selectedArticles}
          onClose={() => {
            setSelectedDate(null);
            setSelectedArticles([]);
            const next = new URLSearchParams(searchParams);
            next.delete('date');
            setSearchParams(next, { replace: false });
          }}
          onArticleSelect={setSelectedArticle}
          isMilitaryMode={isMilitaryMode}
          language={language}
        />
      )}

      {/* Help Modal */}
      <HelpModal 
        isOpen={showHelp} 
        onClose={() => {
          setShowHelp(false);
          try {
            const next = new URLSearchParams(searchParams);
            next.delete('help');
            setSearchParams(next, { replace: false });
          } catch {}
        }} 
        language={language} 
      />

      {/* Top subscribe button removed; bottom banner remains */}

      {/* Article Detail Modal */}
      {selectedArticle && (
        <ArticleDetailModal
          article={selectedArticle}
          language={language}
          onClose={() => {
            setSelectedArticle(null);
            const next = new URLSearchParams(searchParams);
            next.delete('article');
            setSearchParams(next, { replace: false });
          }}
        />
      )}

      {/* Footer Links */}
      <footer className="mt-8 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-600">
            <Link to={`/${(i18n.language || 'et').split('-')[0]}/terms`} className="hover:underline">{t('legal.terms.title')}</Link>
            <span className="mx-2">•</span>
            <Link to={`/${(i18n.language || 'et').split('-')[0]}/privacy`} className="hover:underline">{t('legal.privacy.title')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
